import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useReveal, useTilt } from '../shell';
import { categoryLabel, filters, isLive, projects } from '../data';

const ProjectLinks = ({ project, size = 'm-btn--sm' }) => (
  <>
    {isLive(project.github) ? (
      <a
        className={`m-btn m-btn--secondary ${size}`}
        href={project.github}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        <i className="fab fa-github" aria-hidden="true" /> Code
      </a>
    ) : (
      <span className={`m-btn m-btn--secondary ${size} is-disabled`} aria-disabled="true">
        Private
      </span>
    )}

    {isLive(project.demo) ? (
      <a
        className={`m-btn m-btn--primary ${size}`}
        href={project.demo}
        target={project.demo.startsWith('http') ? '_blank' : undefined}
        rel={project.demo.startsWith('http') ? 'noopener noreferrer' : undefined}
        download={project.demo.startsWith('http') ? undefined : true}
        onClick={(e) => e.stopPropagation()}
      >
        Live demo
      </a>
    ) : (
      <span className={`m-btn m-btn--primary ${size} is-disabled`} aria-disabled="true">
        Demo soon
      </span>
    )}
  </>
);

/**
 * The card carries its own links now, so it can no longer be one big <button>
 * — an anchor inside a button is invalid and unreachable by keyboard. The
 * preview is the button; the links sit beside it in the same bordered box.
 */
const Card = ({ project, onOpen }) => {
  const tilt = useTilt();

  return (
    <article ref={tilt} className="m-card m-reveal">
      <button type="button" className="m-card__open" onClick={() => onOpen(project)}>
        <span className="m-card__media">
          <img
            src={project.image}
            alt={project.title}
            loading="lazy"
            decoding="async"
            width="640"
            height="400"
          />
        </span>
        <span className="m-card__body">
          <span className="m-card__kicker">{categoryLabel[project.category]}</span>
          <span className="m-card__title">{project.title}</span>
          <span className="m-card__short">{project.short}</span>
          <span className="m-tags">
            {project.tech.map((t) => (
              <span key={t} className="m-tag">
                {t}
              </span>
            ))}
          </span>
          <span className="m-card__stats">
            {project.stats.map((s) => (
              <span key={s}>{s}</span>
            ))}
          </span>
        </span>
      </button>

      <div className="m-card__links">
        <ProjectLinks project={project} />
        <button type="button" className="m-card__more" onClick={() => onOpen(project)}>
          Details
        </button>
      </div>
    </article>
  );
};

const Modal = ({ project, onClose }) => {
  const panelRef = useRef(null);

  // Escape to close, and keep focus inside while it is open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    panelRef.current?.focus();

    const { body } = document;
    const prev = body.style.overflow;
    body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKey);
      body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div className="m-modal" onClick={onClose} role="presentation">
      <div
        ref={panelRef}
        className="m-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-label={project.title}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <img src={project.image} alt={project.title} />
        <div className="m-modal__body">
          <div className="m-card__kicker">{categoryLabel[project.category]}</div>
          <h2 className="m-modal__title">{project.title}</h2>
          <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--color-neutral-800)', margin: 0 }}>
            {project.description}
          </p>
          <div className="m-tags">
            {project.tech.map((t) => (
              <span key={t} className="m-tag m-tag--outline">
                {t}
              </span>
            ))}
          </div>
          <div className="m-modal__actions">
            <ProjectLinks project={project} size="" />
          </div>
        </div>
        <button type="button" className="m-modal__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
    </div>
  );
};

const Projects = () => {
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null);

  const shown = useMemo(
    () => (filter === 'all' ? projects : projects.filter((p) => p.category === filter)),
    [filter]
  );

  // Re-run the reveal when the filter changes, since new cards mount.
  useReveal([filter]);

  const close = useCallback(() => setModal(null), []);

  return (
    <>
      <section className="m-head">
        <div className="m-head__kicker">Projects</div>
        <h1 className="m-head__title">
          Digital
          <br />
          creations
        </h1>
        <p className="m-head__lead">
          Machine learning, web development and the odd desktop tool. Hover a card — it lifts off
          the grid.
        </p>
      </section>

      <div className="m-filters" role="group" aria-label="Filter projects">
        {filters.map((f) => (
          <button
            key={f.id}
            type="button"
            className={`m-filter${filter === f.id ? ' is-active' : ''}`}
            aria-pressed={filter === f.id}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Announced to screen readers when the filter changes. Clipped rather
          than pushed to left:-9999px — an off-screen element still occupies
          layout space and reports a position outside the viewport, which is
          indistinguishable from a genuine overflow bug. */}
      <p className="m-sr-only" role="status" aria-live="polite">
        {shown.length} projects shown
      </p>

      <section className="m-section--grid">
        <div className="m-cards">
          {shown.map((p) => (
            <Card key={p.id} project={p} onOpen={setModal} />
          ))}
        </div>
      </section>

      {modal && <Modal project={modal} onClose={close} />}
    </>
  );
};

export default Projects;
